import React, { useState } from "react";
import { Link, Redirect, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import IntlMessages from "@iso/components/utility/intlMessages";
import StopDeviceAlarmRequestWrapper from "./StopDeviceAlarmRequest.styles";
import { Button, Spin } from "antd";
import logo from "@iso/assets/images/logo.png";
import queryString from "query-string";
import { formService, alertDeviceService } from "@iso/services";
import StopDeviceAlarmRequestModal from "./StopDeviceAlarmRequestModal";

// View modes — drives which block the page renders. Mirrors
// SafefarmRespond, the newer public response page, which already had to
// solve "the link outlives the alarm".
const VIEW_LOADING = "loading";
const VIEW_FORM = "form";        // terminate form shown
const VIEW_DONE = "done";        // this visit terminated the alarm
const VIEW_RESOLVED = "resolved"; // alarm was already terminated
const VIEW_RATE_LIMIT = "rateLimit";
const VIEW_INVALID = "invalid";

export default function StopDeviceAlarmRequest() {
  const isLoggedIn = useSelector((state) => state.Auth.idToken);
  const token = queryString.parse(window.location.search).token || "";
  const [visibleStopDeviceAlarmRequestModal, setVisibleStopDeviceAlarmRequestModal] = useState(true);
  const [view, setView] = useState(VIEW_LOADING);

  React.useEffect(() => {
    let cancelled = false;
    if (!token) {
      setView(VIEW_INVALID);
      return undefined;
    }
    // publicGetByToken is the auth-exempt lookup (see AlertDeviceController
    // ::behaviors). The old call here hit api-alert-device-notifications
    // /get-by-token on a controller that does NOT exempt auth, so it 401'd
    // for every logged-out recipient — which is all of them — and threw an
    // unhandled rejection. Its result was never rendered anyway.
    alertDeviceService
      .publicGetByToken(token)
      .then((res) => {
        if (cancelled) return;
        if (!res || res.code !== "0000") {
          setView(VIEW_INVALID);
          return;
        }
        // The token never expires and is never consumed, so a re-opened
        // email link would otherwise re-present a form whose submit
        // silently does nothing and still reports success.
        if (res.is_resolved) {
          setView(VIEW_RESOLVED);
          return;
        }
        // Resolved wins over rate-limited: "it's handled" is the more
        // useful thing to tell someone whose alarm is already over.
        if (
          typeof res.submission_count === "number" &&
          typeof res.submission_limit === "number" &&
          res.submission_count >= res.submission_limit
        ) {
          setView(VIEW_RATE_LIMIT);
          return;
        }
        setView(VIEW_FORM);
      })
      .catch(() => {
        // Fail open: this lookup is a courtesy, not a gate. If it can't
        // be reached, still let the recipient terminate — blocking a
        // safety-critical cancel on a non-essential precheck is worse
        // than showing the form one extra time.
        if (!cancelled) setView(VIEW_FORM);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const cancelModal = () => {
    setVisibleStopDeviceAlarmRequestModal(false);
  };

  const onTerminated = (alreadyResolved) => {
    setVisibleStopDeviceAlarmRequestModal(false);
    setView(alreadyResolved ? VIEW_RESOLVED : VIEW_DONE);
  };

  const onRateLimited = () => {
    setVisibleStopDeviceAlarmRequestModal(false);
    setView(VIEW_RATE_LIMIT);
  };

  // No link on the logo in these states — the recipient is usually not
  // logged in, and sending them to /dashboard would bounce them straight
  // to the signup screen these cards exist to avoid.
  const renderStateCard = (title, body) => (
    <StopDeviceAlarmRequestWrapper className="isoSignInPage">
      <div className="isoLoginContentWrapper">
        <div className="isoLoginContent">
          <div className="isoLogoWrapper">
            <img src={logo} height="40" alt="AirAgri" />
          </div>
          <div className="stateCard">
            {title ? <h2>{title}</h2> : null}
            {body ? <p>{body}</p> : <Spin />}
          </div>
        </div>
      </div>
    </StopDeviceAlarmRequestWrapper>
  );

  if (view === VIEW_LOADING) {
    return renderStateCard(null, null);
  }

  if (view === VIEW_DONE) {
    return renderStateCard(
      "Alarm terminated",
      "The alarm has been terminated. No further action is needed."
    );
  }

  if (view === VIEW_RESOLVED) {
    return renderStateCard(
      "Alarm already resolved",
      "This alarm has already been terminated. No further action is needed."
    );
  }

  if (view === VIEW_RATE_LIMIT) {
    return renderStateCard(
      "Link no longer accepting responses",
      "Too many submissions on this link. If the alarm is still active, please respond from the AirAgri app."
    );
  }

  if (view === VIEW_INVALID) {
    return renderStateCard(
      "Link not valid",
      "This alarm link is invalid or has expired."
    );
  }

  return (
    <StopDeviceAlarmRequestWrapper className="isoSignInPage">
      {!isLoggedIn &&
      <div className="isoLoginContentWrapper">
        <div className="isoLoginContent">
          <div className="isoLogoWrapper">
            <Link to="/dashboard">
              <img src={logo} height="40" />
            </Link>
          </div>
          <div className="isoSignInForm">
            <Link to="/signin" className="isoForgotPass">
              <Button type="primary" className="btn-success">
                <IntlMessages id="page.signInButton" />
              </Button>
            </Link>

            <div className="isoCenterComponent isoHelperWrapper">
              <Link to={`/signup`}>
                <IntlMessages id="page.signInCreateAccount" />
              </Link>
            </div>
          </div>
        </div>
      </div>}
      <StopDeviceAlarmRequestModal
        visibleStopDeviceAlarmRequestModal={visibleStopDeviceAlarmRequestModal}
        cancelModal={cancelModal}
        onTerminated={onTerminated}
        onRateLimited={onRateLimited}
      />
    </StopDeviceAlarmRequestWrapper>
  );
}
